/**
 * Resolving an asoebi assignment back to the fabrics it actually consumes.
 *
 * `fabric_orders.fabric_id` is overloaded: it holds EITHER a fabric (product)
 * id or a bundle id, depending on how the assignment was created, and the
 * record's own `fabric_kind` says which. Which table to look in is decided by
 * `fabricRef`'s helpers (never by trying one table and falling back to the
 * other — the two id spaces overlap); what to do with the row once found
 * lives here, once, instead of being re-improvised at each call site.
 */
import { Fabric, FabricAssignment } from '../types/ui/Asoebi';
import { UnifiedBundle } from '../types/Product';
import { fabricRefId, isBundleRef, refersToBundle } from './fabricRef';

export interface FabricAttribution {
  /** fabric id → catalogue units attributed (yards, or pieces for accessories). */
  unitsByFabric: Record<string, number>;
  /**
   * Ids of bundles that assignments point at but whose items resolve to no
   * catalogue fabric at all. Their cost is genuinely unknown here rather than
   * zero, so callers can say so instead of silently under-reporting.
   */
  unresolvedBundleIds: string[];
  /** Assignment ids whose fabric_id matches neither a fabric nor a bundle. */
  unresolvedAssignmentIds: string[];
}

/**
 * Spreads each assignment's quantity over the fabrics it consumes.
 *
 * - Direct fabric assignment: `yards` is literally yards of that fabric.
 * - Bundle assignment: the same `yards` column holds the number of sets
 *   ordered, so each fabric in the bundle picks up `item.quantity × yards`.
 *
 * `fabric_kind` decides which of the two an assignment is; a row predating
 * that column falls back to the old bundles-win-on-a-collision precedence.
 */
export function attributeFabricUnits(
  assignments: FabricAssignment[],
  fabrics: Fabric[],
  bundles: UnifiedBundle[],
): FabricAttribution {
  const fabricIds = new Set(fabrics.map(f => String(f.id)));
  const bundleById = new Map(bundles.map(b => [String(b.id), b]));

  const unitsByFabric: Record<string, number> = {};
  const unresolvedBundleIds = new Set<string>();
  const unresolvedAssignmentIds: string[] = [];

  assignments.forEach(a => {
    const targetId = fabricRefId(a);
    // No target, or a zero/negative quantity — nothing to attribute either way.
    if (!targetId) return;
    const quantity = Number(a.yards) || 0;
    if (quantity <= 0) return;

    if (isBundleRef(a, bundles)) {
      const bundle = bundleById.get(targetId);
      // Says bundle, names no bundle we hold — unresolvable, not a fabric.
      if (!bundle) { unresolvedAssignmentIds.push(a.id); return; }
      let matchedAnyFabric = false;
      bundle.items.forEach(item => {
        const productId = String(item.productId ?? '');
        if (!productId || !fabricIds.has(productId)) return;
        matchedAnyFabric = true;
        unitsByFabric[productId] =
          (unitsByFabric[productId] ?? 0) + (item.quantity || 0) * quantity;
      });
      if (!matchedAnyFabric) unresolvedBundleIds.add(targetId);
      return;
    }

    if (fabricIds.has(targetId)) {
      unitsByFabric[targetId] = (unitsByFabric[targetId] ?? 0) + quantity;
      return;
    }

    unresolvedAssignmentIds.push(a.id);
  });

  return {
    unitsByFabric,
    unresolvedBundleIds: [...unresolvedBundleIds],
    unresolvedAssignmentIds,
  };
}

/** Total cost of the attributed units, priced at each fabric's cost per unit. */
export function totalFabricCost(fabrics: Fabric[], attribution: FabricAttribution): number {
  return fabrics.reduce(
    (sum, f) => sum + (Number(f.price) || 0) * (attribution.unitsByFabric[String(f.id)] ?? 0),
    0,
  );
}

export interface BundleSellability {
  bundleId: string;
  bundleName: string;
  /** Sets makeable from raw stock alone, ignoring what's already promised
   *  elsewhere — the theoretical ceiling. */
  makeableFromStock: number;
  /** Sets actually safe to sell right now without overselling — raw stock
   *  net of every fabric already committed across ALL active assignments
   *  (this bundle's own past sales, direct fabric sales, and any other
   *  bundle sharing the same fabric), divided by this bundle's recipe. This
   *  is `activeAssignments`' attribution, so a fabric two different bundles
   *  both use is only counted as "spoken for" once, not once per bundle. */
  sellableNow: number;
  /** Sets already reserved against THIS bundle specifically (assignments
   *  whose fabric_id is this bundle's id), any payment status. */
  reservedSets: number;
  /** Of reservedSets, how many are marked paid. */
  paidSets: number;
  /** Whichever fabric item is the tightest constraint on sellableNow — null
   *  when the bundle has no items that resolve to a real fabric at all. */
  bottleneckFabric: string | null;
}

/**
 * Per-bundle sellable-units, net of what every other assignment (bundle or
 * direct-fabric) has already committed against the same fabric stock — the
 * "can I promise one more of this to a new guest without overselling"
 * number GuestAsoebiTracker/BundlesView have never had.
 */
export function computeBundleSellability(
  bundles: UnifiedBundle[],
  fabrics: Fabric[],
  assignments: FabricAssignment[],
  committedAttribution: FabricAttribution,
): BundleSellability[] {
  const fabricById = new Map(fabrics.map(f => [String(f.id), f]));

  return bundles.map(bundle => {
    let makeableFromStock = Infinity;
    let sellableNow = Infinity;
    let bottleneckFabric: string | null = null;

    bundle.items.forEach(item => {
      const fabric = fabricById.get(String(item.productId));
      if (!fabric || !item.quantity) return;
      const grossSets = Math.floor((fabric.stock || 0) / item.quantity);
      const committed = committedAttribution.unitsByFabric[String(fabric.id)] ?? 0;
      const netSets = Math.floor(Math.max(0, (fabric.stock || 0) - committed) / item.quantity);
      if (grossSets < makeableFromStock) makeableFromStock = grossSets;
      if (netSets < sellableNow) {
        sellableNow = netSets;
        bottleneckFabric = fabric.name;
      }
    });
    if (makeableFromStock === Infinity) makeableFromStock = 0;
    if (sellableNow === Infinity) sellableNow = 0;

    // An id match alone would also pull in a direct-fabric assignment whose
    // product id happens to equal this bundle's id — refersToBundle checks
    // fabric_kind too, so those stay out of this bundle's reserved/paid sets.
    const bundleAssignments = assignments.filter(a => !a.archived && refersToBundle(a, bundle.id, bundles));
    const reservedSets = bundleAssignments.reduce((sum, a) => sum + (Number(a.yards) || 0), 0);
    const paidSets = bundleAssignments
      .filter(a => a.payment_status === 'paid')
      .reduce((sum, a) => sum + (Number(a.yards) || 0), 0);

    return {
      bundleId: bundle.id,
      bundleName: bundle.name,
      makeableFromStock,
      sellableNow,
      reservedSets,
      paidSets,
      bottleneckFabric,
    };
  });
}
